import React from "react";
import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { META_CONFIG } from "@/config/meta";
import { queryClient } from "@/lib/queryClient";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isMetaConnected: boolean;
  connectMeta: () => void;
  checkMetaConnection: (userId: string) => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMetaConnected, setIsMetaConnected] = useState(false);

  useEffect(() => {
    // Handle ephemeral session (Remember me = OFF)
    const isEphemeral = localStorage.getItem('tbd_ephemeral_session') === '1';
    const sessionStillAlive = sessionStorage.getItem('tbd_ephemeral_session') === '1';

    if (isEphemeral && !sessionStillAlive) {
      localStorage.removeItem('tbd_ephemeral_session');
      supabase.auth.signOut({ scope: 'local' }).then(() => {
        setUser(null);
        setIsMetaConnected(false);
        setLoading(false);
      });
      return;
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkMetaConnection(session.user.id);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkMetaConnection(session.user.id);
      } else {
        setIsMetaConnected(false);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Refresh session when tab becomes visible again (after sleep/background)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session } }) => {
          setUser(session?.user ?? null);
          if (session?.user) {
            checkMetaConnection(session.user.id);
          } else {
            setIsMetaConnected(false);
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const checkMetaConnection = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("meta_oauth_tokens")
        .select("expires_at")
        .eq("user_id", userId)
        .maybeSingle();

      if (error) {
        console.error("Error checking Meta connection:", error);
        setIsMetaConnected(false);
        return;
      }

      if (data && data.expires_at) {
        const expiresAt = new Date(data.expires_at);
        const now = new Date();
        setIsMetaConnected(expiresAt > now);
      } else {
        setIsMetaConnected(false);
      }
    } catch (err) {
      console.error("Error checking Meta connection:", err);
      setIsMetaConnected(false);
    }
  };

  const connectMeta = () => {
    const state = Math.random().toString(36).substring(7);
    const params = new URLSearchParams({
      client_id: META_CONFIG.APP_ID,
      redirect_uri: META_CONFIG.REDIRECT_URI,
      response_type: "code",
      config_id: META_CONFIG.CONFIG_ID,
      state,
    });
    window.location.href = `https://www.facebook.com/v24.0/dialog/oauth?${params.toString()}`;
  };

  const signIn = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    if (error) {
      console.error("Error signing in:", error);
    }
  };

  const signOut = async () => {
    // Remove all Realtime channels BEFORE signOut to prevent stale subscriptions
    supabase.removeAllChannels();

    try {
      // Try the normal (global) sign-out first.
      await supabase.auth.signOut();
    } catch (error) {
      // When the server-side session is already gone, Supabase can return
      // `session_not_found`. We still must clear the local persisted session.
      console.log("Sign out error (session may already be expired):", error);
    } finally {
      // Always clear the local session (localStorage + in-memory) so the app
      // can't "re-hydrate" a stale session on next render.
      try {
        await supabase.auth.signOut({ scope: "local" });
      } catch {
        // ignore
      }
    }

    // Clear React Query cache so the new session starts fresh
    queryClient.clear();

    // Always clear local state regardless of API response
    setUser(null);
    setIsMetaConnected(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isMetaConnected,
        connectMeta,
        checkMetaConnection,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
